import { Button, Col, DatePicker, Dropdown, Input, Menu, notification, Popconfirm, Row, Table, Tag } from "antd";
import { MenuOutlined, PlusOutlined } from '@ant-design/icons';
import { useEffect, useState } from "react";
import "./style/Appointments.scss";
import { setHeaderName } from "../../services/SetHeaderName";
import moment from "moment";
import { listOfAppointmentsApi, updateAppointmentApi } from "../../config/Api";
import { ADMIN_ROLE, ROLE_KEYWORD } from "../../config/constant";
import { readCookie } from "../../config/CookieScript";
import { apiErrorMessage } from "../../services/ApiError";
import { BookAppointment } from "./components/BookAppointment";

const STATUS_COLORS = { Scheduled: 'blue', Completed: 'green', Cancelled: 'default', 'No-show': 'red' }

// "2026-09-27" -> "27 Sep, 2026"
const showDate = (date) => (date ? moment(date, 'YYYY-MM-DD').format('DD MMM, YYYY') : '')
// "14:30" -> "02:30 PM"
const showTime = (time) => moment(time, 'HH:mm').format('hh:mm A')

export const Appointments = () => {
    const { RangePicker } = DatePicker;
    const isAdmin = readCookie(ROLE_KEYWORD) == ADMIN_ROLE

    const [selectedDate, setSelectedDate] = useState([moment(), moment()]);
    const [appointments, setAppointments] = useState([])
    const [search, setSearch] = useState('')
    const [loading, setLoading] = useState(false)
    const [bookingOpen, setBookingOpen] = useState(false)
    const [rescheduling, setRescheduling] = useState(null)

    const getAppointments = () => {
        if (!selectedDate || selectedDate.length !== 2) return
        setLoading(true)
        listOfAppointmentsApi(selectedDate[0].format('YYYY-MM-DD'), selectedDate[1].format('YYYY-MM-DD'))
            .then((res) => setAppointments(res.data.data || []))
            .catch((err) => notification.error({ message: apiErrorMessage(err) }))
            .finally(() => setLoading(false))
    }

    useEffect(() => {
        setHeaderName("Appointments")
    }, []);

    useEffect(() => {
        getAppointments()
    }, [selectedDate])

    const setStatus = (record, status) => {
        updateAppointmentApi(record.id, { status })
            .then(() => {
                notification.success({ message: `Appointment marked ${status.toLowerCase()}.` })
                getAppointments()
            })
            .catch((err) => notification.error({ message: apiErrorMessage(err) }))
    }

    const filtered = appointments.filter((row) => {
        const text = search.trim().toLowerCase()
        return !text || row.patient_name.toLowerCase().includes(text) || (row.program || '').toLowerCase().includes(text)
    })

    const columns = [
        {
            title: 'Date & Time',
            key: 'when',
            sorter: (a, b) => (a.date + a.start_time).localeCompare(b.date + b.start_time),
            defaultSortOrder: 'ascend',
            render: (_, row) => (
                <>
                    <p className="mb-0">{showDate(row.date)}</p>
                    <p className="mb-0 text-muted">{showTime(row.start_time)} - {showTime(row.end_time)}</p>
                </>
            ),
        },
        {
            title: 'Client Name',
            dataIndex: 'patient_name',
            key: 'patient_name',
            sorter: (a, b) => a.patient_name.localeCompare(b.patient_name),
        },
        isAdmin && {
            title: 'Nutritionist',
            dataIndex: 'nutritionist',
            key: 'nutritionist',
            sorter: (a, b) => a.nutritionist.localeCompare(b.nutritionist),
        },
        {
            title: 'Type',
            dataIndex: 'type',
            key: 'type',
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            render: (status) => <Tag color={STATUS_COLORS[status]}>{status}</Tag>,
        },
        {
            title: 'Preferred Call Time',
            dataIndex: 'preferred_call_time',
            key: 'preferred_call_time',
            render: (text) => <p className="mb-0" style={{ color: !text ? 'red' : undefined }}>{text || 'Not Set'}</p>,
        },
        {
            title: 'Last Diet Sent On',
            dataIndex: 'last_diet_sent_on',
            key: 'last_diet_sent_on',
            render: (date) => (date ? showDate(date) : <span className="text-muted">Never</span>),
        },
        {
            title: 'Program',
            dataIndex: 'program',
            key: 'program',
            sorter: (a, b) => (a.program || '').localeCompare(b.program || ''),
        },
        {
            title: 'Notes',
            dataIndex: 'notes',
            key: 'notes',
            render: (notes) => <p className="mb-0" style={{ whiteSpace: 'pre-wrap', maxWidth: 260 }}>{notes}</p>,
        },
        {
            title: 'Action',
            key: 'action',
            render: (_, row) =>
                // only the nutritionist who owns an appointment can change it; the admin views everyone's
                row.can_edit && row.status === 'Scheduled' ? (
                    <Dropdown
                        trigger={['click']}
                        overlay={
                            <Menu style={{ minWidth: 180 }}>
                                <Menu.Item key="reschedule" onClick={() => setRescheduling(row)}>Reschedule</Menu.Item>
                                <Menu.Item key="completed" onClick={() => setStatus(row, 'Completed')}>Mark completed</Menu.Item>
                                <Menu.Item key="noshow" onClick={() => setStatus(row, 'No-show')}>Mark no-show</Menu.Item>
                                <Menu.Item key="cancel">
                                    <Popconfirm title="Cancel this appointment?" okText="Yes" cancelText="No" onConfirm={() => setStatus(row, 'Cancelled')}>
                                        <span className="d-block">Cancel</span>
                                    </Popconfirm>
                                </Menu.Item>
                            </Menu>
                        }
                    >
                        <Button icon={<MenuOutlined />} />
                    </Dropdown>
                ) : null,
        },
    ].filter(Boolean);

    return (
        <>
            <div className="Appointments">
                <Row className="mb-2" gutter={[8, 8]}>
                    <Col xs={24} md={8} xl={6} className="align-self-center">
                        <RangePicker value={selectedDate} onChange={setSelectedDate} format="DD-MM-YYYY" allowClear={false} />
                    </Col>
                    <Col xs={24} md={6} xl={6} className="align-self-center">
                        <Button type="primary" icon={<PlusOutlined />} onClick={() => setBookingOpen(true)}>
                            New Appointment
                        </Button>
                    </Col>
                    <Col xs={24} md={4} xl={6} className="text-end align-self-center pe-4">
                        <h6 className="mb-0">Total Appointments : {filtered.length}</h6>
                    </Col>
                    <Col xs={24} md={6} xl={6} className="align-self-center pe-2">
                        <Input.Search placeholder="Search client or program" onChange={(e) => setSearch(e.target.value)} className="p-2" allowClear />
                    </Col>
                </Row>
                <Row>
                    <Table
                        columns={columns}
                        dataSource={filtered}
                        rowKey="id"
                        loading={loading}
                        scroll={{ x: "max-content" }}
                        className="w-100"
                        locale={{ emptyText: 'No appointments in these dates' }}
                    />
                </Row>
            </div>

            <BookAppointment
                open={bookingOpen || Boolean(rescheduling)}
                appointment={rescheduling}
                onClose={() => { setBookingOpen(false); setRescheduling(null) }}
                onSaved={getAppointments}
            />
        </>
    )
}
